import type { ExpenseCategory, RecurringExpense } from './mock-data'
import { getLang, KA_MONTHS, LOCALES, t, type Key } from './i18n'

/**
 * 121 — monthly costs (ყოველთვიური ხარჯები): rent, salary and utilities
 * entered once and written every month.
 *
 * The DATABASE decides what books and when (`book_due_recurring_expenses`,
 * daily at 08:00 Tbilisi, and `save_recurring_expense` at once when a day has
 * come). These functions only answer the screen's questions — „when is the
 * next one", „what is waiting for an amount" — with the same arithmetic as
 * `private.day_in_month / next_month_day / first_day_from`, pinned to the same
 * cases in recurring-expenses.test.ts.
 *
 * Dates are 'YYYY-MM-DD' strings: calendar days in Tbilisi, never instants.
 */

const pad = (n: number) => String(n).padStart(2, '0')
const iso = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`   // m is 1–12
const parts = (s: string) => {
  const [y, m, d] = s.slice(0, 10).split('-').map(Number)
  return { y, m, d }
}
const daysIn = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate()

/** Day `day` of `month`'s month; 29–31 fall on a shorter month's last day. */
export function dayInMonth(month: string, day: number): string {
  const { y, m } = parts(month)
  return iso(y, m, Math.min(day, daysIn(y, m)))
}

/** The occurrence after `due`: day `day` of the next month. */
export function nextMonthDay(due: string, day: number): string {
  const { y, m } = parts(due)
  return m === 12 ? dayInMonth(iso(y + 1, 1, 1), day) : dayInMonth(iso(y, m + 1, 1), day)
}

/** The first occurrence on or after `today`. */
export function firstDayFrom(today: string, day: number): string {
  const here = dayInMonth(today, day)
  return here >= today ? here : nextMonthDay(today, day)
}

/** Today in Tbilisi — the database's `(now() AT TIME ZONE 'Asia/Tbilisi')::date`. */
export function tbilisiToday(now: Date = new Date()): string {
  // en-CA prints YYYY-MM-DD.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Tbilisi', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(now)
}

/** Its day has already passed this month — the only time the form asks
 *  „this month too?". On the day itself it books without asking. */
export function dayPassedThisMonth(today: string, day: number): boolean {
  return dayInMonth(today, day) < today
}

/** Where a NEW cost's first occurrence falls — what the form promises. */
export function firstOccurrence(today: string, day: number, thisMonth: boolean): string {
  return thisMonth && dayPassedThisMonth(today, day) ? dayInMonth(today, day) : firstDayFrom(today, day)
}

export { waitingForAmount, comingUp, addDays } from './recurring-due'

/** What the active monthly costs come to — auto: the amount; ask: last month's. */
export function monthlyTotal(list: RecurringExpense[]): number {
  return Math.round(list.filter((r) => r.active).reduce((s, r) => s + r.amount, 0) * 100) / 100
}

/** The costs in the order the list shows them: running before paused, then by
 *  the day of the month. */
export function sortRecurring(list: RecurringExpense[]): RecurringExpense[] {
  return [...list].sort((a, b) =>
    Number(b.active) - Number(a.active) || a.day_of_month - b.day_of_month || a.created_at.localeCompare(b.created_at))
}

const MONTHS_OF = ['იანვრის', 'თებერვლის', 'მარტის', 'აპრილის', 'მაისის', 'ივნისის', 'ივლისის', 'აგვისტოს', 'სექტემბრის', 'ოქტომბრის', 'ნოემბრის', 'დეკემბრის']

// Georgian keeps its own month words (the genitive „სექტემბრის" is not what
// Intl gives); another language lets Intl say the day and the month.
const intl = (date: string, opts: Intl.DateTimeFormatOptions) => {
  const { y, m, d } = parts(date)
  return new Intl.DateTimeFormat(LOCALES[getLang()], { ...opts, timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, d)))
}

/** „5 ოქტომბერი" / „5 октября" */
export function dayLabel(date: string): string {
  if (getLang() !== 'ka') return intl(date, { day: 'numeric', month: 'long' })
  const { m, d } = parts(date)
  return `${d} ${KA_MONTHS[m - 1]}`
}

/** „სექტემბრის" — whose month an amount is for („სექტემბრის კომუნალური");
 *  another language gets the month's name („сентябрь"), its sentences are
 *  built around it. */
export function monthOf(date: string): string {
  if (getLang() !== 'ka') return intl(date, { month: 'long' })
  return MONTHS_OF[parts(date).m - 1]
}

/** A monthly cost's name for one month: „სექტემბრის კომუნალური" in Georgian,
 *  „Коммунальные · сентябрь" in a language whose word order differs. */
export function costTitle(category: ExpenseCategory, date: string): string {
  const cat = t(`expenseCat.${category}` as Key)
  return getLang() === 'ka' ? `${monthOf(date)} ${cat}` : `${cat} · ${monthOf(date)}`
}

/** „ყოველი თვის 5 რიცხვში"; 31 is „the end of the month". */
export function everyMonthLabel(day: number): string {
  return day >= 31 ? t('rec.everyLast') : t('rec.everyDay', { day })
}
